<script lang="ts">
  import Modal from '@shared/ui/Modal.svelte';
  import { createProductCompetitorService } from '@shared/api/products';
  import { ApiError } from '@shared/api/client';
  import { competitorServiceOptionsStore } from '@entities/competitor-service/store.svelte';
  import { filterProductCompetitorCandidates } from '@domain/competitor-service/product-competitors-edit';
  import type { ProductCompetitorService } from '@shared/types/product-competitor-service';

  interface Props {
    open: boolean;
    productId: string;
    existingRows: ProductCompetitorService[];
    onClose: () => void;
    onCreated: (row: ProductCompetitorService) => void;
    onConflict: () => Promise<void>;
  }

  let { open, productId, existingRows, onClose, onCreated, onConflict }: Props =
    $props();

  const LEGACY_FALLBACK_STORAGE_PREFIX =
    'lpcrm:product-competitor-legacy-fallback-master-ids:';

  function fallbackStorageKey(id: string): string {
    return `${LEGACY_FALLBACK_STORAGE_PREFIX}${encodeURIComponent(id)}`;
  }

  function loadLegacyFallbackMasterIds(id: string): string[] {
    if (typeof window === 'undefined') return [];
    try {
      const stored = window.sessionStorage.getItem(fallbackStorageKey(id));
      if (stored === null) return [];
      const parsed: unknown = JSON.parse(stored);
      return Array.isArray(parsed)
        ? [...new Set(parsed.filter((value): value is string => typeof value === 'string'))]
        : [];
    } catch {
      return [];
    }
  }

  function rememberLegacyFallbackMasterId(
    submittedProductId: string,
    masterId: string,
  ): void {
    const rememberedIds = [
      ...new Set([
        ...loadLegacyFallbackMasterIds(submittedProductId),
        ...(legacyFallbackMasterIdsByProduct[submittedProductId] ?? []),
        masterId,
      ]),
    ];
    legacyFallbackMasterIdsByProduct = {
      ...legacyFallbackMasterIdsByProduct,
      [submittedProductId]: rememberedIds,
    };
    try {
      window.sessionStorage.setItem(
        fallbackStorageKey(submittedProductId),
        JSON.stringify(rememberedIds),
      );
    } catch {
      // Candidate filtering still works for the current mount when storage is
      // unavailable (for example, due to browser privacy settings).
    }
  }

  let searchQuery = $state('');
  let selectedId = $state('');
  let submittingProductIds = $state<string[]>([]);
  let serverError = $state<string | null>(null);
  // A legacy backend can only create a free-text card, so its response cannot
  // carry the selected master ID. Remember that intent for this browser session
  // and do not offer the same master again during the rolling-deploy window.
  let legacyFallbackMasterIdsByProduct = $state<Record<string, string[]>>({
    [productId]: loadLegacyFallbackMasterIds(productId),
  });
  let fallbackIdsProductId = productId;

  let submitting = $derived(submittingProductIds.includes(productId));
  let legacyFallbackMasterIds = $derived(
    legacyFallbackMasterIdsByProduct[productId] ?? [],
  );

  let candidates = $derived.by(() => {
    const fallbackIds = new Set(legacyFallbackMasterIds);
    return filterProductCompetitorCandidates(
      competitorServiceOptionsStore.data,
      existingRows,
      searchQuery,
    ).filter((candidate) => !fallbackIds.has(candidate.id));
  });

  $effect(() => {
    if (fallbackIdsProductId !== productId) {
      fallbackIdsProductId = productId;
      if (!(productId in legacyFallbackMasterIdsByProduct)) {
        legacyFallbackMasterIdsByProduct = {
          ...legacyFallbackMasterIdsByProduct,
          [productId]: loadLegacyFallbackMasterIds(productId),
        };
      }
    }
    if (!open) return;
    resetSelection();
    void competitorServiceOptionsStore.load();
  });

  $effect(() => {
    if (selectedId === '') return;
    if (!candidates.some((candidate) => candidate.id === selectedId)) {
      selectedId = '';
    }
  });

  function resetSelection(): void {
    searchQuery = '';
    selectedId = '';
    serverError = null;
  }

  function close(): void {
    if (submitting) return;
    resetSelection();
    onClose();
  }

  async function retryLoad(): Promise<void> {
    if (submitting) return;
    serverError = null;
    await competitorServiceOptionsStore.load();
  }

  async function handleSubmit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (submitting) return;

    const selected = candidates.find(
      (candidate) => candidate.id === selectedId,
    );
    if (!selected) {
      serverError = '候補から競合ソリューションを選択してください';
      return;
    }

    submittingProductIds = [...submittingProductIds, productId];
    serverError = null;
    const submittedProductId = productId;
    try {
      const row = await createProductCompetitorService(
        submittedProductId,
        {
          competitor_service_id: selected.id,
        },
        selected.name,
      );
      if (row.competitor_service_id === null) {
        rememberLegacyFallbackMasterId(submittedProductId, selected.id);
      }
      // The editor may be reused for another product while the request is in
      // flight. Keep the result scoped to the product that initiated it.
      if (productId !== submittedProductId) return;
      onCreated(row);
      resetSelection();
    } catch (error) {
      if (productId !== submittedProductId) return;
      serverError =
        error instanceof Error
          ? error.message
          : '紐づけに失敗しました。再試行してください。';
      if (error instanceof ApiError && error.status === 409) {
        selectedId = '';
        await onConflict();
      }
    } finally {
      submittingProductIds = submittingProductIds.filter(
        (id) => id !== submittedProductId,
      );
    }
  }
</script>

<Modal {open} title="競合ソリューションを紐づける" onClose={close}>
  <form id="product-competitor-link-form" onsubmit={handleSubmit}>
    <label class="search-field">
      <span>外部ソリューション名検索</span>
      <input
        type="search"
        placeholder="外部ソリューション名で検索"
        bind:value={searchQuery}
        autocomplete="off"
        disabled={submitting}
        data-testid="product-competitor-search"
      />
    </label>

    <section
      class="candidate-section"
      aria-label="未紐づけ競合ソリューション候補"
    >
      <div class="candidate-heading">
        <span>候補</span>
        <small data-testid="product-competitor-candidate-count"
          >{candidates.length}件</small
        >
      </div>

      {#if competitorServiceOptionsStore.loading}
        <p
          class="empty-candidates"
          data-testid="product-competitor-candidates-loading"
        >
          候補を読み込んでいます。
        </p>
      {:else if competitorServiceOptionsStore.error}
        <div class="load-error" role="alert">
          <p>{competitorServiceOptionsStore.error}</p>
          <button
            type="button"
            onclick={() => void retryLoad()}
            disabled={submitting}
          >
            再試行
          </button>
        </div>
      {:else if candidates.length === 0}
        <p
          class="empty-candidates"
          data-testid="product-competitor-candidates-empty"
        >
          {searchQuery.trim().length > 0
            ? '検索条件に一致する競合ソリューションがありません'
            : '紐付け可能な競合ソリューションがありません'}
        </p>
      {:else}
        <div class="candidate-list" data-testid="product-competitor-candidates">
          {#each candidates as candidate (candidate.id)}
            <button
              type="button"
              class:selected={selectedId === candidate.id}
              aria-pressed={selectedId === candidate.id}
              disabled={submitting}
              onclick={() => (selectedId = candidate.id)}
              data-testid="product-competitor-candidate"
            >
              <span class="candidate-kind">競合</span>
              <span class="candidate-name">{candidate.name}</span>
            </button>
          {/each}
        </div>
      {/if}
    </section>

    {#if serverError}
      <p
        class="server-error"
        data-testid="product-competitor-link-error"
        role="alert"
      >
        {serverError}
      </p>
    {/if}
  </form>

  {#snippet footer()}
    <button type="button" onclick={close} disabled={submitting}
      >キャンセル</button
    >
    <button
      type="submit"
      form="product-competitor-link-form"
      class="primary"
      disabled={submitting ||
        competitorServiceOptionsStore.loading ||
        competitorServiceOptionsStore.error !== null ||
        selectedId === ''}
      data-testid="product-competitor-link-submit"
    >
      {submitting ? '保存中…' : '競合ソリューションを紐づけ'}
    </button>
  {/snippet}
</Modal>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .search-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: #374151;
    font-size: 0.875rem;
  }
  .search-field > span {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
  }
  input {
    width: 100%;
    box-sizing: border-box;
    padding: 9px 10px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font: inherit;
    font-size: 13px;
  }
  input:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
    outline: none;
  }
  .candidate-section {
    display: grid;
    gap: 8px;
  }
  .candidate-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    color: #374151;
    font-size: 13px;
    font-weight: 700;
  }
  .candidate-heading small {
    color: #64748b;
    font-weight: 600;
  }
  .candidate-list {
    display: grid;
    gap: 6px;
    max-height: 400px;
    overflow-y: auto;
  }
  .candidate-list button {
    display: grid;
    gap: 3px;
    width: 100%;
    min-width: 0;
    padding: 8px 10px;
    text-align: left;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #fff;
    color: #111827;
    cursor: pointer;
    font: inherit;
  }
  .candidate-list button:hover:not(:disabled) {
    border-color: #1d4ed8;
    background: #eff6ff;
  }
  .candidate-list button:focus-visible {
    outline: 2px solid #60a5fa;
    outline-offset: 2px;
  }
  .candidate-list button.selected {
    border-color: #1d4ed8;
    background: #eff6ff;
    box-shadow: 0 0 0 1px #1d4ed8 inset;
  }
  .candidate-list button:disabled,
  button:disabled,
  input:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  .candidate-kind {
    color: #64748b;
    font-size: 11px;
    line-height: 1.4;
  }
  .candidate-name {
    color: #111827;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.4;
    overflow-wrap: anywhere;
  }
  .empty-candidates,
  .load-error {
    margin: 0;
    padding: 16px;
    color: #64748b;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 6px;
    font-size: 13px;
    text-align: center;
  }
  .load-error {
    display: grid;
    justify-items: center;
    gap: 10px;
    color: #b91c1c;
  }
  .load-error p {
    margin: 0;
  }
  .server-error {
    color: #b91c1c;
    margin: 0;
    font-size: 13px;
  }
  button {
    font: inherit;
  }
  button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;
  }
</style>
